import { Text, VSpacer } from '@/components/DesignSystem';
import {
  Checkbox,
  Container,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import en from '../../../locales/en.json';
import frCa from '../../../locales/fr-CA.json';

interface TranslationRow {
  key: string;
  en: string;
  frCa: string;
}

const flattenObject = (obj: Record<string, unknown>, prefix = ''): Record<string, string> => {
  return Object.entries(obj).reduce<Record<string, string>>((acc, [key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(acc, flattenObject(value as Record<string, unknown>, fullKey));
    } else {
      acc[fullKey] = String(value ?? '');
    }
    return acc;
  }, {});
};

const buildRows = (): TranslationRow[] => {
  const flatEn = flattenObject(en);
  const flatFrCa = flattenObject(frCa);
  const allKeys = Array.from(new Set([...Object.keys(flatEn), ...Object.keys(flatFrCa)])).sort();
  return allKeys.map((key) => ({
    en: flatEn[key] ?? '',
    frCa: flatFrCa[key] ?? '',
    key,
  }));
};

const AllRows = buildRows();

const cellStyle: React.CSSProperties = {
  maxWidth: 400,
  overflowWrap: 'break-word',
  verticalAlign: 'top',
  whiteSpace: 'pre-wrap',
};

export const TranslationExplorer = () => {
  const [search, setSearch] = useState('');
  const [showMissingOnly, setShowMissingOnly] = useState(false);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return AllRows.filter(
      (r) =>
        (!showMissingOnly || !r.frCa)
        && (!q
          || r.key.toLowerCase().includes(q)
          || r.en.toLowerCase().includes(q)
          || r.frCa.toLowerCase().includes(q)
          || r.frCa.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').includes(q)
        ),
    );
  }, [search, showMissingOnly]);

  return (
    <Container maxWidth="xl">
      <VSpacer size="5" />
      <Text category="title-large">Translation Explorer</Text>
      <VSpacer size="3" />
      <TextField
        fullWidth
        label="Search keys or values"
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        value={search}
        variant="outlined"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={showMissingOnly}
            onChange={(e) => setShowMissingOnly(e.target.checked)}
            size="small"
          />
        }
        label="Show missing fr-CA only"
      />
      <VSpacer size="3" />
      <Text category="body-small">
        {filteredRows.length} of {AllRows.length} keys
      </Text>
      <VSpacer size="2" />
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {[
                { label: 'Key', minWidth: 280 },
                { label: 'English (en)', minWidth: 200 },
                { label: 'French Canadian (fr-CA)', minWidth: 200 },
              ].map(({ label, minWidth }) => (
                <TableCell key={label} style={{ fontWeight: 700, minWidth }}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => (
              <TableRow hover key={row.key}>
                <TableCell style={{ ...cellStyle, fontFamily: 'monospace', fontSize: 12 }}>
                  {row.key}
                </TableCell>
                <TableCell style={cellStyle}>{row.en}</TableCell>
                <TableCell
                  style={{
                    ...cellStyle,
                    color: row.frCa ? 'inherit' : '#c62828',
                    fontStyle: row.frCa ? 'normal' : 'italic',
                  }}
                >
                  {row.frCa || 'MISSING'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <VSpacer size="5" />
    </Container>
  );
};
